import { DateTime } from "luxon";
import { getPlanDateRangeOrNull } from "../../../features/Booking-Form/functions/floor.functions";
import { BookingType } from "../../../features/Booking-Form/typings/booking-inputs";
import { defaultRecurrenceWeekdays } from "../../../features/Booking-Form/typings/shared/weekdays";
import type { BookingScheduleInterface } from "../../../features/Booking-Form/typings/booking.types";
import type { GetBookingFloorParams } from "../../../features/Booking-Form/thunks/booking.thunks";
import type { FloorAvailability } from "../../../features/FloorManager/typings/floor-inventory.entity";
import type { ISeatStatus } from "../../../components/FacilityManager/Domain/Types/FloorPlan/SeatStatus.type";
import type { IZoneInventory } from "../../../components/FacilityManager/Domain/Types/FloorPlan/ZoneInventory.type";
import type { IFloorPayload } from "../../../components/FacilityManager/Domain/Types/FloorPlan/FloorPayload.type";
import {
  filterListByCategory,
  tempFilterListByCategory
} from "../../../components/BookingForm/functions/form.functions";
import {
  GroupFilterSelected,
  GroupFilterType
} from "../../../components/BookingForm/BookingFilter/types/GroupFilters.type";
import { hasServicePlanExists } from "../../../components/FacilityManager/Components/Views/SelectFloorPlanView/BookingServicePrompt/functions/hasMandatory.function";
import type { BookingInputs } from "../../../components/FacilityManager/Domain/Types/BookingInputs.type";
import type { ServiceOrderDto } from "../../../components/FacilityManager/Components/Views/SelectFloorPlanView/BookingServicePrompt/Domain/BookingServicePrompt.types";
import {
  ServiceDeliveryStatus,
  ServiceType
} from "../../../features/Booking-Form/typings/booking-service-administration.types";
import type { BookingServicesDto } from "../../../features/Booking-Form/typings/booking.types";
import { BookingRequestsStatus } from "../../../components/BookingRequest/typings/booking-requests.types";
import type { UpdateServicesDto } from "../../../hooks/Remote/Services/OrderRequests/useRemoteUpdateBookingServicesByUser";

/** Pixi fill for a newly picked conference room (change-room floor step only). */
export const CHANGE_ROOM_SELECTED_ZONE_FILL_COLOR = 0x0027eb;

/** Pixi fill for the booking's current room (change-room floor step only). */
export const CHANGE_ROOM_CURRENT_ZONE_FILL_COLOR = 0xff9800;

/** Fill opacity for the current booking room highlight (change-room floor step only). */
export const CHANGE_ROOM_CURRENT_ZONE_FILL_ALPHA = 0.55;

export type CanShowChangeConferenceRoomParams = {
  schedule: BookingScheduleInterface;
  isEditable: boolean;
  isColleagueScheduleEditable: boolean;
  activateBookingServices?: boolean;
};

export function isChangeRoomSubjectEditable(schedule: BookingScheduleInterface): boolean {
  const startIso = scheduleStartIso(schedule);
  if (!startIso) {
    return false;
  }
  const startDt = DateTime.fromISO(startIso);
  return startDt.isValid && startDt.toUTC().valueOf() > DateTime.now().toUTC().valueOf();
}

export function normalizeChangeRoomSubject(value: string): string {
  return value.trim();
}

export function isChangeRoomSubjectValid(value: string): boolean {
  return normalizeChangeRoomSubject(value).length > 0;
}

export function canShowChangeConferenceRoomButton({
  schedule,
  isEditable,
  isColleagueScheduleEditable
}: CanShowChangeConferenceRoomParams): boolean {
  const mayEdit = isEditable || isColleagueScheduleEditable;
  const isConference = schedule.bookingType === BookingType.CONFERENCEZONE;
  const locationNotMobileWorking = schedule.locationName !== "Mobile Working";
  const notVisitOnly = schedule.isVisitOnly !== true;

  return mayEdit && isConference && locationNotMobileWorking && notVisitOnly;
}

export function resolveFloorInventoryIdFromSchedule(
  schedule: BookingScheduleInterface
): number | undefined {
  const inv = schedule.bookingInventory as IZoneInventory | undefined;
  const fromInv = inv?.floorInventoryId;
  if (fromInv != null && fromInv > 0) {
    return fromInv;
  }
  return undefined;
}

export function resolveLocationInventoryIdFromSchedule(
  schedule: BookingScheduleInterface
): number | null {
  if (schedule.locationInventoryId != null) {
    return schedule.locationInventoryId;
  }
  const inv = schedule.bookingInventory as IZoneInventory | undefined;
  return inv?.locationInventory?.id ?? inv?.floorInventory?.locationInventoryId ?? null;
}

function scheduleStartIso(schedule: BookingScheduleInterface): string {
  return schedule.start ?? schedule.startDate;
}

function scheduleEndIso(schedule: BookingScheduleInterface): string {
  return schedule.end ?? schedule.endDate;
}

/** Date range for `/plan/.../services` from an existing conference booking. */
export function getChangeRoomPlanDateRange(
  schedule: BookingScheduleInterface
): { end: string; start: string } | null {
  const startDt = DateTime.fromISO(scheduleStartIso(schedule));
  const endDt = DateTime.fromISO(scheduleEndIso(schedule));
  if (!startDt.isValid || !endDt.isValid) {
    return null;
  }
  return getPlanDateRangeOrNull(
    startDt.toFormat("yyyy-MM-dd"),
    startDt.toFormat("HH:mm"),
    endDt.toFormat("yyyy-MM-dd"),
    endDt.toFormat("HH:mm")
  );
}

/** Build floor availability request from an existing conference booking (no Redux booking slice). */
export function buildChangeRoomFloorAvailabilityArgs(
  schedule: BookingScheduleInterface
): GetBookingFloorParams | null {
  const floorInventoryId = resolveFloorInventoryIdFromSchedule(schedule);
  const locationInventoryId = resolveLocationInventoryIdFromSchedule(schedule);
  const startIso = scheduleStartIso(schedule);
  const endIso = scheduleEndIso(schedule);

  if (!floorInventoryId || locationInventoryId == null || !startIso || !endIso) {
    return null;
  }

  const startDt = DateTime.fromISO(startIso);
  const endDt = DateTime.fromISO(endIso);

  return {
    users: [
      {
        userId: schedule.userBookedFor,
        firstName: "",
        surname: "",
        isExternal: false
      }
    ],
    startDate: startDt.toFormat("yyyy-MM-dd"),
    endDate: endDt.toFormat("yyyy-MM-dd"),
    startTime: startDt.toFormat("HH:mm"),
    endTime: endDt.toFormat("HH:mm"),
    bookingType: BookingType.CONFERENCEZONE,
    locationInventoryId,
    description: "change-conference-room",
    floorInventoryId,
    weekdays: defaultRecurrenceWeekdays,
    frequence: undefined,
    conferenceZoneBookingGuests: schedule.guests?.map(g => ({
      userId: g.userId,
      isRequired: g.required
    }))
  };
}

export function buildSyntheticBookingInputsFromSchedule(
  schedule: BookingScheduleInterface
): BookingInputs {
  const startIso = scheduleStartIso(schedule);
  const endIso = scheduleEndIso(schedule);
  const startDt = DateTime.fromISO(startIso);
  const endDt = DateTime.fromISO(endIso);

  return {
    mode: BookingType.CONFERENCEZONE,
    bookingType: BookingType.CONFERENCEZONE,
    bookingFrom: startDt.toFormat("yyyy-MM-dd"),
    bookingTo: endDt.toFormat("yyyy-MM-dd"),
    bookingStart: startDt.toFormat("HH:mm"),
    bookingEnd: endDt.toFormat("HH:mm"),
    timezone: schedule.timezone,
    weekdays: defaultRecurrenceWeekdays,
    usersBookedFor: [{ userId: schedule.userBookedFor, isExternal: false }],
    zoneAccess: schedule.guests?.map(g => ({
      userId: g.userId,
      isRequired: g.required,
      isExternal: g.external ?? false
    })),
    selectedLocation: resolveLocationInventoryIdFromSchedule(schedule) ?? undefined,
    floorInventoryId: resolveFloorInventoryIdFromSchedule(schedule)
  } as unknown as BookingInputs;
}

export function mapFloorAvailabilityToSeatStatus(
  floor: FloorAvailability | undefined
): ISeatStatus {
  if (!floor) {
    return {
      availableList: [],
      tempList: [],
      occupiedList: [],
      disableList: [],
      restrictedList: []
    };
  }

  return {
    availableList: floor.availableInventoryIds ?? [],
    tempList: floor.availableInventoryIds ?? [],
    occupiedList: floor.occupiedInventoryIds ?? [],
    disableList: floor.disabledInventoryIds ?? [],
    restrictedList: floor.forbiddenInventoryIds ?? []
  };
}

export type ChangeRoomBookingFilters = {
  equipmentCategories: { id: number; quantity: number }[];
  placeCategoryIds: number[];
  zoneCategoryIds: number[];
  propertyIds: number[];
};

export function buildBookingFiltersFromGroupFilterItems(
  filterItems: GroupFilterSelected[]
): ChangeRoomBookingFilters {
  const equipCate = filterItems.filter(item => item.type === GroupFilterType.EQUIPMENTCATEGORY);
  const placeCate = filterItems.filter(item => item.type === GroupFilterType.PLACECATEGORY);
  const zoneCate = filterItems.filter(item => item.type === GroupFilterType.ZONECATEGORY);
  const bookProp = filterItems.filter(item => item.type === GroupFilterType.BOOKINGPROPERTY);

  return {
    equipmentCategories: equipCate.map(c => ({ id: Number(c.id), quantity: Number(c.quantity) })),
    placeCategoryIds: placeCate.map(c => Number(c.id)),
    zoneCategoryIds: zoneCate.map(c => Number(c.id)),
    propertyIds: bookProp.map(p => Number(p.id))
  };
}

export function mergeBookingFiltersIntoInputs(
  base: BookingInputs,
  bookingFilters: ChangeRoomBookingFilters | undefined
): BookingInputs {
  const usersBookedFor = base.usersBookedFor?.map((user, index) =>
    index === 0 ? { ...user, bookingFilters } : user
  );

  return {
    ...base,
    bookingFilters,
    usersBookedFor
  } as BookingInputs;
}

export function computeChangeRoomSeatStatus(
  floor: FloorAvailability | undefined,
  filteredSelectables: number[] | undefined
): ISeatStatus {
  if (!floor) {
    return mapFloorAvailabilityToSeatStatus(undefined);
  }

  return {
    availableList: filterListByCategory(floor.availableInventoryIds ?? [], filteredSelectables),
    tempList: tempFilterListByCategory(floor.availableInventoryIds ?? [], filteredSelectables),
    occupiedList: floor.occupiedInventoryIds ?? [],
    disableList: floor.disabledInventoryIds ?? [],
    restrictedList: floor.forbiddenInventoryIds ?? []
  };
}

export function resolveCurrentBookingInventoryId(
  schedule: BookingScheduleInterface
): number | undefined {
  if (schedule.bookingInventoryId != null && schedule.bookingInventoryId > 0) {
    return schedule.bookingInventoryId;
  }
  const inv = schedule.bookingInventory as IZoneInventory | undefined;
  return inv?.id;
}

export function findZoneScheduleIdByInventoryId(
  floorPlan: IFloorPayload | undefined,
  inventoryId: number | undefined
): number | undefined {
  if (!floorPlan || inventoryId == null) {
    return undefined;
  }
  return floorPlan.zones.find(z => z.inventory?.id === inventoryId || z.inventoryId === inventoryId)
    ?.id;
}

export function findZoneDisplayName(
  floorPlan: IFloorPayload | undefined,
  inventoryId: number | undefined,
  fallback?: string
): string {
  if (!floorPlan || inventoryId == null) {
    return fallback ?? "";
  }
  const zone = floorPlan.zones.find(z => z.inventory?.id === inventoryId);
  return zone?.inventory?.name ?? fallback ?? "";
}

export function newRoomRequiresServiceReselection(
  floorPlanForService: IFloorPayload | undefined,
  newZoneScheduleId: number | undefined,
  activateBookingServices: boolean
): boolean {
  if (!activateBookingServices || newZoneScheduleId == null) {
    return false;
  }
  return hasServicePlanExists({
    floorPlanForService,
    selectedZone: newZoneScheduleId
  });
}

export function canProceedFromChangeRoomServicesStep({
  servicesSectionLoading,
  configurableServiceTabCount,
  hasIncompleteMandatory
}: {
  servicesSectionLoading: boolean;
  configurableServiceTabCount: number;
  hasIncompleteMandatory: boolean;
}): boolean {
  if (servicesSectionLoading) {
    return false;
  }
  if (configurableServiceTabCount === 0) {
    return true;
  }
  return !hasIncompleteMandatory;
}

export function serviceOrderToUpdateServicesDto(
  schedule: BookingScheduleInterface,
  order: ServiceOrderDto
): UpdateServicesDto {
  const defaultServiceStatus =
    schedule.serviceBookings?.[0]?.status ?? BookingRequestsStatus.APPROVED;

  const caterings: BookingServicesDto[] = order.caterings
    .filter(c => c.qty > 0)
    .map(cat => ({
      serviceType: ServiceType.Catering,
      status: defaultServiceStatus,
      serviceId: cat.id,
      setUpTime: cat.setUpTime ?? 15,
      qty: cat.qty,
      orderedQty: cat.qty
    }));

  const equipments: BookingServicesDto[] = order.bookableEquipments.map(equip => ({
    serviceType: ServiceType.Bookable_Equipment,
    status: defaultServiceStatus,
    serviceId: equip.id,
    setUpTime: equip.setUpTime ?? 15,
    qty: 1
  }));

  const layouts: BookingServicesDto[] = order.roomLayout.map(layout => ({
    serviceType: ServiceType.Room_Layout,
    status: defaultServiceStatus,
    serviceId: layout.id,
    setUpTime: layout.setUpTime ?? 15
  }));

  return {
    bookingId: String(schedule.id),
    bookingType: BookingType.CONFERENCEZONE,
    deliveryStatus: schedule.serviceBookings?.[0]?.deliveryStatus ?? ServiceDeliveryStatus.ORDERED,
    servicesInfo: [...caterings, ...equipments, ...layouts]
  };
}
